import { type AiClientContext, trackAi } from '@/_shared/lib/analytics';

export type PromptSource = 'composer' | 'starter_suggestion' | 'follow_up_action';

export interface TurnOutcome {
  status: 'success' | 'error' | 'timeout' | 'cancelled';
  errorKind?: 'agent_error';
}

interface SubmittedTurn {
  conversationId: string;
  submittedAt: number;
  firstRenderAt: number | null;
}

/** Router state ChatWelcome hands a new chat's first turn over in. */
export interface PendingTurnRouterState {
  pendingTurnId?: string;
  aiTurn?: { submittedAt: number; timeOrigin: number };
}

/**
 * The handed-over turn to report the answer copy for. History state survives a reload, where the old
 * `submittedAt` is meaningless and a replayed terminal event would send a second answer copy.
 */
export function adoptableRouterTurn(
  state: PendingTurnRouterState | null,
  timeOrigin: number = performance.timeOrigin,
): { turnId: string; submittedAt: number } | null {
  if (!state?.pendingTurnId || state.aiTurn?.timeOrigin !== timeOrigin) return null;
  return { turnId: state.pendingTurnId, submittedAt: state.aiTurn.submittedAt };
}

/** The `ai_user_prompt_created` client copy for a turn the server accepted. */
export function sendPromptCopy(
  turnId: string,
  conversationId: string,
  source: PromptSource,
  length: number,
  context: AiClientContext,
): void {
  trackAi(
    'ai_user_prompt_created',
    {
      interaction_id: turnId,
      conversation_id: conversationId,
      prompt_source: source,
      prompt_length: length,
    },
    context,
  );
}

/** The outcome a terminal SSE event shows. An error inside a streamed turn is `agent_error`. */
export function sseTerminalOutcome(eventType: string, data: Record<string, unknown>): TurnOutcome | null {
  if (eventType === 'turn_aborted') return { status: 'cancelled' };
  if (eventType === 'error') return { status: 'error', errorKind: 'agent_error' };
  if (eventType === 'turn_complete') {
    if (
      data['outcome'] === 'failed' ||
      (typeof data['responseStatus'] === 'string' && data['responseStatus'] !== 'success')
    ) {
      // Client schema v0.2 has no incomplete status: a capped answer is an agent error here.
      return { status: data['responseStatus'] === 'timeout' ? 'timeout' : 'error', errorKind: 'agent_error' };
    }
    return data['outcome'] === 'interrupted' ? { status: 'error', errorKind: 'agent_error' } : { status: 'success' };
  }
  return null;
}

/** The outcome of a turn status Postgres confirmed after a Stop. */
export function turnStatusOutcome(status: string): TurnOutcome | null {
  if (status === 'completed') return { status: 'success' };
  if (status === 'aborted') return { status: 'cancelled' };
  if (status === 'failed' || status === 'interrupted') return { status: 'error', errorKind: 'agent_error' };
  return null;
}

// Turns whose answer copy this page load already sent. Page-wide, because navigating back to New chat
// and forward again mounts a new reporter over the same router state and a replayed terminal event.
const MAX_ANSWERED_TURNS = 500;
const answeredTurns = new Set<string>();

function markAnswered(turnId: string): void {
  answeredTurns.add(turnId);
  // A Set iterates in insertion order, so the first entry is the oldest.
  const oldest = answeredTurns.values().next().value;
  if (answeredTurns.size > MAX_ANSWERED_TURNS && oldest !== undefined) answeredTurns.delete(oldest);
}

/** Test-only: forget which turns sent their answer copy. */
export function resetAnsweredTurnsForTest(): void {
  answeredTurns.clear();
}

/**
 * Client copies of `ai_user_prompt_created` / `ai_response_received` for turns this tab submitted.
 * A turn leaves the map on its first final state, so stream replay, reconnect recovery and the Stop
 * confirmation cannot send a second answer copy.
 */
export function createAiTurnReporter(context: AiClientContext, now: () => number = () => performance.now()) {
  const turns = new Map<string, SubmittedTurn>();
  return {
    submitted(turnId: string, conversationId: string, submittedAt: number, source: PromptSource, length: number): void {
      if (turns.has(turnId)) return;
      turns.set(turnId, { conversationId, submittedAt, firstRenderAt: null });
      sendPromptCopy(turnId, conversationId, source, length, context);
    },
    /** A turn another view submitted and sent the prompt copy for: report only its answer copy. */
    adopt(turnId: string, conversationId: string, submittedAt: number): void {
      if (turns.has(turnId) || answeredTurns.has(turnId)) return;
      turns.set(turnId, { conversationId, submittedAt, firstRenderAt: null });
    },
    rendered(turnId: string): void {
      const turn = turns.get(turnId);
      if (turn?.firstRenderAt === null) turn.firstRenderAt = now();
    },
    finished(turnId: string, outcome: TurnOutcome | null): void {
      const turn = turns.get(turnId);
      if (!turn || !outcome) return;
      turns.delete(turnId);
      if (answeredTurns.has(turnId)) return;
      markAnswered(turnId);
      // A turn that ends before any answer content renders measures to its final state instead.
      const renderedAt = turn.firstRenderAt ?? now();
      trackAi(
        'ai_response_received',
        {
          interaction_id: turnId,
          conversation_id: turn.conversationId,
          response_status: outcome.status,
          client_error_kind: outcome.errorKind,
          time_to_first_render_ms: Math.max(0, Math.round(renderedAt - turn.submittedAt)),
        },
        context,
      );
    },
  };
}
